import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { PublicShipmentForDrivers, Shipment } from "../types";

// Resolve carrier/truck/driver safely
function resolveCarrierValue(
  value: any,
  type: "truck" | "trailer" | "driver",
): string {
  if (!value) return "-";

  if (typeof value === "string") return value;

  if (typeof value === "object") {
    switch (type) {
      case "truck":
      case "trailer":
        return value?.registrationNumber || "-";
      case "driver":
        return (
          `${value?.firstName || ""} ${value?.lastName || ""}`.trim() || "-"
        );
    }
  }

  return "-";
}

// Safe helper for nested properties
function safe<T>(fn: () => T, fallback: string = "-"): string {
  try {
    const val = fn();
    return val !== undefined && val !== null && val !== ""
      ? String(val)
      : fallback;
  } catch {
    return fallback;
  }
}

export async function createDeliveryNotePdf(
  data: Shipment | PublicShipmentForDrivers,
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();

  // PDF metadata
  pdfDoc.setTitle(
    `Delivery Note${data.referenceNumber ? ` - ${data.referenceNumber}` : ""}`,
  );
  pdfDoc.setAuthor("LiveCargo");
  pdfDoc.setSubject("Delivery Note Document");
  pdfDoc.setCreationDate(new Date());

  const page = pdfDoc.addPage([595, 842]); // A4
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const margin = 50;
  let yPos = height - margin;

  const black = rgb(0, 0, 0);
  const gray = rgb(0.4, 0.4, 0.4);
  const lightGray = rgb(0.95, 0.95, 0.95);
  const headerBg = rgb(0.9, 0.9, 0.9);

  // Title
  const title = safe(
    () => `DELIVERY NOTE ${data.shipmentNumber}`,
    "DELIVERY NOTE",
  );
  const titleWidth = boldFont.widthOfTextAtSize(title, 20);
  page.drawText(title, {
    x: (width - titleWidth) / 2,
    y: yPos,
    size: 20,
    font: boldFont,
    color: black,
  });
  yPos -= 20;

  // Reference number
  const refLabel = "Reference: ";
  const refValue = safe(() => data.referenceNumber);
  const refLabelWidth = font.widthOfTextAtSize(refLabel, 10);
  const refValueWidth = boldFont.widthOfTextAtSize(refValue, 10);
  const refStartX = (width - (refLabelWidth + refValueWidth)) / 2;

  page.drawText(refLabel, {
    x: refStartX,
    y: yPos,
    size: 10,
    font,
    color: gray,
  });
  page.drawText(refValue, {
    x: refStartX + refLabelWidth,
    y: yPos,
    size: 10,
    font: boldFont,
    color: black,
  });
  yPos -= 30;

  // Separator
  page.drawLine({
    start: { x: margin, y: yPos + 10 },
    end: { x: width - margin, y: yPos + 10 },
    thickness: 1,
    color: rgb(0.8, 0.8, 0.8),
  });
  yPos -= 10;

  const colWidth = (width - 2 * margin - 10) / 2;
  const sectionHeight = 90;

  // Section drawer (fully safe)
  const drawSection = (
    title: string,
    companyName: string | undefined,
    address: string | undefined,
    x: number,
    y: number,
    boxWidth: number,
    extraLines?: { label: string; value: string }[],
    dateTime?: string,
  ) => {
    page.drawText(title, { x: x + 5, y, size: 9, font: boldFont, color: gray });

    if (dateTime) {
      const dtWidth = font.widthOfTextAtSize(dateTime, 8);
      page.drawText(dateTime, {
        x: x + boxWidth - dtWidth - 5,
        y,
        size: 8,
        font,
        color: gray,
      });
    }

    page.drawText(
      safe(() => companyName),
      { x: x + 5, y: y - 18, size: 10, font: boldFont, color: black },
    );

    let currentY = y - 32;
    if (address) {
      address.split(", ").forEach((word) => {
        if (currentY >= y - sectionHeight + 10) {
          page.drawText(word, {
            x: x + 5,
            y: currentY,
            size: 9,
            font,
            color: black,
          });
          currentY -= 12;
        }
      });
    }

    if (extraLines) {
      currentY -= 5;
      for (const line of extraLines) {
        if (currentY < y - sectionHeight + 10) break;
        page.drawText(line.label, {
          x: x + 5,
          y: currentY,
          size: 9,
          font,
          color: gray,
        });
        page.drawText(
          safe(() => line.value),
          { x: x + 50, y: currentY, size: 9, font: boldFont, color: black },
        );
        currentY -= 12;
      }
    }

    page.drawRectangle({
      x,
      y: y - sectionHeight + 15,
      width: boxWidth,
      height: sectionHeight,
      borderColor: rgb(0.8, 0.8, 0.8),
      borderWidth: 0.5,
    });
  };

  // Row 1: Pickup (optional) and Delivery (mandatory)
  drawSection(
    "PICKUP",
    safe(() => data.pickup?.companyName),
    safe(() => data.pickup?.address?.street),
    margin,
    yPos,
    colWidth,
    undefined,
    safe(() =>
      [data.pickup?.date.split("T")[0], data.pickup?.time]
        .filter(Boolean)
        .join(", "),
    ),
  );

  drawSection(
    "DELIVERY",
    safe(() => data.delivery.companyName),
    safe(() => data.delivery.address?.street),
    margin + colWidth + 10,
    yPos,
    colWidth,
    undefined,
    safe(() =>
      [data.delivery?.date.split("T")[0], data.delivery?.time]
        .filter(Boolean)
        .join(", "),
    ),
  );

  yPos -= sectionHeight + 10;

  // Row 2: Client (optional) and Carrier (optional)
  drawSection(
    "CLIENT / SUPPLIER",
    safe(() => data.client?.companyName),
    safe(() => data.client?.address?.street),
    margin,
    yPos,
    colWidth,
  );

  const carrierExtraLines = [
    {
      label: "Truck:",
      value: resolveCarrierValue(data.carrier?.truckLicensePlate, "truck"),
    },
    {
      label: "Trailer:",
      value: resolveCarrierValue(data.carrier?.trailerLicensePlate, "trailer"),
    },
    {
      label: "Driver:",
      value: resolveCarrierValue(data.assignedDriver, "driver"),
    },
  ];

  drawSection(
    "CARRIER",
    safe(() => data.carrier?.companyName),
    "",
    margin + colWidth + 10,
    yPos,
    colWidth,
    carrierExtraLines,
  );

  yPos -= sectionHeight + 20;

  // Draw a separator line
  page.drawLine({
    start: { x: margin, y: yPos + 10 },
    end: { x: width - margin, y: yPos + 10 },
    thickness: 1,
    color: rgb(0.8, 0.8, 0.8),
  });

  yPos -= 10;

  // Cargo Items Table
  page.drawText("CARGO ITEMS", {
    x: margin,
    y: yPos,
    size: 9,
    font: boldFont,
    color: gray,
  });
  yPos -= 25;

  // Table setup
  const tableWidth = width - 2 * margin;
  const rowHeight = 25;
  const colWidths = {
    qty: 60,
    type: 80,
    weight: 100,
    description: tableWidth - 240, // Remaining space
  };

  // Table Header
  page.drawRectangle({
    x: margin,
    y: yPos - rowHeight + 5,
    width: tableWidth,
    height: rowHeight,
    color: headerBg,
  });

  let colX = margin + 10;
  page.drawText("QTY", {
    x: colX,
    y: yPos - 15,
    size: 9,
    font: boldFont,
    color: gray,
  });
  colX += colWidths.qty;

  page.drawText("TYPE", {
    x: colX,
    y: yPos - 15,
    size: 9,
    font: boldFont,
    color: gray,
  });
  colX += colWidths.type;

  page.drawText("WEIGHT (KG)", {
    x: colX,
    y: yPos - 15,
    size: 9,
    font: boldFont,
    color: gray,
  });
  colX += colWidths.weight;

  page.drawText("DESCRIPTION", {
    x: colX,
    y: yPos - 15,
    size: 9,
    font: boldFont,
    color: gray,
  });

  // Header bottom line
  page.drawLine({
    start: { x: margin, y: yPos - rowHeight + 5 },
    end: { x: margin + tableWidth, y: yPos - rowHeight + 5 },
    thickness: 1,
    color: rgb(0.8, 0.8, 0.8),
  });

  yPos -= rowHeight;

  // Table rows
  let totalQty = 0;
  let totalWeight = 0;

  for (let i = 0; i < data.cargoItems.length; i++) {
    const item = data.cargoItems[i];
    yPos -= rowHeight;

    // Alternate row background
    if (i % 2 === 1) {
      page.drawRectangle({
        x: margin,
        y: yPos + 5,
        width: tableWidth,
        height: rowHeight,
        color: lightGray,
      });
    }

    colX = margin + 10;

    // Quantity
    const qty = item.quantity || 0;
    totalQty += qty;
    page.drawText(`${item.quantity}` || "-", {
      x: colX,
      y: yPos + 10,
      size: 10,
      font: font,
      color: black,
    });
    colX += colWidths.qty;

    // Type
    page.drawText(item.type || "-", {
      x: colX,
      y: yPos + 10,
      size: 10,
      font: font,
      color: black,
    });
    colX += colWidths.type;

    // Weight
    const weight = item.weightKg || 0;
    totalWeight += weight;
    page.drawText(`${item.weightKg}` || "-", {
      x: colX,
      y: yPos + 10,
      size: 10,
      font: font,
      color: black,
    });
    colX += colWidths.weight;

    // Description
    const description = item.description || "-";
    const maxDescWidth = colWidths.description - 10;
    const truncatedDesc =
      font.widthOfTextAtSize(description, 10) > maxDescWidth
        ? description.substring(0, 40) + "..."
        : description;
    page.drawText(truncatedDesc, {
      x: colX,
      y: yPos + 10,
      size: 10,
      font: font,
      color: black,
    });

    // Row bottom line
    page.drawLine({
      start: { x: margin, y: yPos + 5 },
      end: { x: margin + tableWidth, y: yPos + 5 },
      thickness: 0.5,
      color: rgb(0.85, 0.85, 0.85),
    });
  }

  // Totals row
  yPos -= rowHeight;
  page.drawRectangle({
    x: margin,
    y: yPos + 5,
    width: tableWidth,
    height: rowHeight,
    color: headerBg,
  });

  colX = margin + 10;
  page.drawText("TOTALS:", {
    x: colX,
    y: yPos + 10,
    size: 10,
    font: boldFont,
    color: black,
  });
  colX += colWidths.qty;

  page.drawText(totalQty.toString(), {
    x: colX,
    y: yPos + 10,
    size: 10,
    font: boldFont,
    color: black,
  });
  colX += colWidths.type;

  page.drawText(`${totalWeight.toLocaleString("en-US")} kg`, {
    x: colX,
    y: yPos + 10,
    size: 10,
    font: boldFont,
    color: black,
  });

  // Totals row bottom line
  page.drawLine({
    start: { x: margin, y: yPos + 5 },
    end: { x: margin + tableWidth, y: yPos + 5 },
    thickness: 1,
    color: rgb(0.8, 0.8, 0.8),
  });

  // Signature section at the bottom
  const signatureY = 100; // Y position from bottom
  const totalWidth = width - 2 * margin;
  const gap = 20; // space between blocks
  const blockWidth = (totalWidth - 2 * gap) / 3;

  // Left: Sender
  page.drawText("Sender Signature:", {
    x: margin,
    y: signatureY + 40,
    size: 9,
    font,
    color: gray,
  });
  page.drawLine({
    start: { x: margin, y: signatureY },
    end: { x: margin + blockWidth, y: signatureY },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });

  // Middle: Carrier
  page.drawText("Carrier Signature:", {
    x: margin + blockWidth + gap,
    y: signatureY + 40,
    size: 9,
    font,
    color: gray,
  });
  page.drawLine({
    start: { x: margin + blockWidth + gap, y: signatureY },
    end: { x: margin + 2 * blockWidth + gap, y: signatureY },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });

  // Right: Receiver
  page.drawText("Receiver Signature:", {
    x: margin + 2 * (blockWidth + gap),
    y: signatureY + 40,
    size: 9,
    font,
    color: gray,
  });
  page.drawLine({
    start: { x: margin + 2 * (blockWidth + gap), y: signatureY },
    end: { x: margin + 3 * blockWidth + 2 * gap, y: signatureY },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });

  // Footer
  page.drawText("Generated by LiveCargo", {
    x: margin,
    y: 40,
    size: 8,
    font: font,
    color: gray,
  });

  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}

export async function downloadDeliveryNotePdf(
  data: Shipment,
  filename?: string,
): Promise<void> {
  const pdfBytes = await createDeliveryNotePdf(data);

  const safeBytes = new Uint8Array(pdfBytes);
  const blob = new Blob([safeBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download =
    filename ||
    `Delivery-Note${data.referenceNumber ? `-${data.referenceNumber}` : ""}.pdf`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
